import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CreditCard, Smartphone, Banknote, Clock, ShieldCheck, Sparkles, ShoppingBag, Coffee, Zap } from "lucide-react";
import { ProductHeader, ProductFooter } from "@/pages/Products";

export default function Residents() {
  useEffect(() => {
    document.title = "Cravy for Residents — Snacks, drinks, and a little joy. 24/7.";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-body bg-cravy-cream min-h-screen">
      <ProductHeader />

      {/* Hero */}
      <section className="bg-cravy-ink cravy-grain pt-[72px] relative overflow-hidden">
        <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-cravy-orange/15 blur-[120px]" />
        <div className="absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full bg-cravy-orangeDeep/10 blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Link to="/" className="text-cravy-cream/60 hover:text-cravy-orange text-[13px] inline-flex items-center gap-2" data-testid="residents-back">
              ← Back to home
            </Link>
            <div className="mt-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cravy-orange/10 border border-cravy-orange/25 text-cravy-orange text-[12px] font-semibold tracking-wide uppercase">
              <Sparkles className="w-3.5 h-3.5" /> For Residents · Employees · Guests
            </div>
            <h1 className="mt-6 font-display font-extrabold text-cravy-cream tracking-[-0.03em] leading-[0.95] text-[44px] sm:text-[64px] lg:text-[80px]">
              Snacks. Drinks. <span className="italic text-cravy-orange">Joy.</span>
              <br />
              Right when you need it.
            </h1>
            <p className="mt-6 text-cravy-cream/75 text-[17px] lg:text-[19px] max-w-xl leading-relaxed">
              Cravy is a 24/7 micro-market your property installs for you. Curated snacks, premium beverages, grab-and-go meals — all checkout in under a minute, no app required.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-[28px] bg-gradient-to-br from-cravy-ink2 to-black/40 border border-white/5 p-3 shadow-2xl float-slow">
              <img src="/assets/starter-market.jpg" alt="Cravy market" className="w-full h-auto rounded-[22px] object-cover" />
              <div className="absolute -bottom-4 left-4 bg-cravy-orange text-cravy-ink rounded-full px-4 py-2 shadow-lg rotate-[-3deg]">
                <div className="font-brush text-[18px] leading-none">Open now.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works for residents */}
      <section className="bg-cravy-cream py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="text-cravy-orangeDeep font-semibold text-[12px] uppercase tracking-[0.25em]">How it works for you</div>
            <h2 className="mt-3 font-display font-extrabold text-[40px] sm:text-[54px] leading-[0.95] tracking-[-0.03em]">
              60 seconds, start to snack.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { n: "01", icon: ShoppingBag, t: "Walk up",    d: "Find your community's Cravy market. Open 24/7 — no hours, no staff." },
              { n: "02", icon: Smartphone,  t: "Tap to pay", d: "Card, tap-to-pay, Apple Pay, Google Pay, or cash. Whatever you've got." },
              { n: "03", icon: Coffee,      t: "Grab and go", d: "Snacks, drinks, meal kits — whatever you're craving in that exact moment." },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-3xl p-7 border border-cravy-ink/5">
                <div className="font-display font-extrabold text-cravy-orange text-[42px] leading-none">{s.n}</div>
                <div className="mt-5 w-11 h-11 rounded-2xl bg-cravy-orange/15 text-cravy-orangeDeep flex items-center justify-center">
                  <s.icon className="w-5 h-5" />
                </div>
                <div className="mt-5 font-display font-bold text-[22px] tracking-tight">{s.t}</div>
                <div className="mt-2 text-cravy-ink/70 text-[14px] leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's in the market */}
      <section className="bg-cravy-cream2 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-2xl mb-12">
            <div className="text-cravy-orangeDeep font-semibold text-[12px] uppercase tracking-[0.25em]">The lineup</div>
            <h2 className="mt-3 font-display font-extrabold text-[40px] sm:text-[54px] leading-[0.95] tracking-[-0.03em]">
              Curated for <span className="italic text-cravy-orangeDeep">your community.</span>
            </h2>
            <p className="mt-5 text-cravy-ink/70 text-[16px] leading-relaxed max-w-lg">
              We mix popular brands with healthier options and crowd-favorites. Tell us what your community wants more of — we adjust.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { t: "Snacks",            d: "Chips, candy, jerky, protein bars, trail mix, chocolate, gum." },
              { t: "Beverages",         d: "Sodas, juices, sparkling water, energy drinks, premium coffees." },
              { t: "Healthier picks",   d: "Veggie crisps, RX bars, kombucha, plant-based snacks, low-sugar." },
              { t: "Grab-and-go meals", d: "Sandwiches, salads, wraps, fruit cups, yogurt — at select locations." },
            ].map((c, i) => (
              <div key={i} className="bg-white rounded-3xl p-6 border border-cravy-ink/5">
                <div className="font-display font-bold text-[18px] tracking-tight">{c.t}</div>
                <div className="mt-2 text-cravy-ink/65 text-[13px] leading-relaxed">{c.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment options */}
      <section className="bg-cravy-ink cravy-grain py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-cravy-orange/8 blur-[140px]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-2xl">
            <div className="text-cravy-orange font-semibold text-[12px] uppercase tracking-[0.25em]">Pay your way</div>
            <h2 className="mt-3 font-display font-extrabold text-cravy-cream text-[40px] sm:text-[54px] leading-[0.95] tracking-[-0.03em]">
              Every checkout method <span className="italic text-cravy-orange">you actually use.</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: CreditCard, t: "Card",       d: "Tap, insert, or swipe." },
              { icon: Smartphone, t: "Phone",      d: "Apple Pay and Google Pay." },
              { icon: Zap,        t: "Contactless", d: "Tap-to-pay on supported coolers." },
              { icon: Banknote,   t: "Cash",       d: "Available on classic vending units." },
            ].map((p, i) => (
              <div key={i} className="rounded-3xl border border-white/8 bg-white/[0.03] p-7 hover:bg-white/[0.06] transition">
                <div className="w-11 h-11 rounded-2xl bg-cravy-orange/15 text-cravy-orange flex items-center justify-center">
                  <p.icon className="w-5 h-5" />
                </div>
                <div className="mt-5 font-display font-bold text-cravy-cream text-[20px]">{p.t}</div>
                <div className="mt-1.5 text-cravy-cream/55 text-[13px]">{p.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For property managers CTA */}
      <section className="bg-cravy-cream py-24 lg:py-28">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cravy-orange/15 border border-cravy-orange/30 text-cravy-orangeDeep text-[12px] font-semibold tracking-wide uppercase">
            <ShieldCheck className="w-3.5 h-3.5" /> Don't have Cravy yet?
          </div>
          <h2 className="mt-6 font-display font-extrabold text-[40px] sm:text-[56px] leading-[0.95] tracking-[-0.03em]">
            Tell your property manager <span className="italic text-cravy-orangeDeep">about Cravy.</span>
          </h2>
          <p className="mt-5 text-cravy-ink/70 text-[16px] max-w-xl mx-auto">
            Forward them this page or — even better — refer them through our referral program and earn a thank-you bonus when they sign up.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/refer" className="btn-orange inline-flex items-center gap-2 px-7 py-4 rounded-full text-[15px]" data-testid="residents-cta-refer">
              Refer a property <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/" className="btn-ghost-dark text-cravy-ink border-cravy-ink/15 hover:bg-cravy-ink/5 inline-flex items-center gap-2 px-7 py-4 rounded-full text-[15px] font-semibold">
              Back to home
            </Link>
          </div>
        </div>
      </section>

      <ProductFooter />
    </div>
  );
}
